import { completeRecoveryMethod } from "@/lib/auth/last-sign-in-method";
import {
	authenticateImportedBackup,
	LoginFlowError,
} from "@/lib/auth/login-flow";
import { RestoreAuthorizationError } from "@/lib/restore-flow";

export interface CloudBackupResponse {
	bapId: string;
	backup: string;
	lastUpdated?: string;
	generation?: number;
}

/** Thrown when the signed-in account simply has no cloud backup to unlock. */
export class NoCloudBackupError extends Error {
	constructor() {
		super("No cloud backup found. Please set up cloud backup first.");
		this.name = "NoCloudBackupError";
	}
}

/** Fetch the signed-in account's encrypted backup from the cloud API. */
export async function fetchCloudBackup(): Promise<CloudBackupResponse> {
	const response = await fetch("/api/backup", {
		method: "GET",
		headers: { "Content-Type": "application/json" },
	});

	if (!response.ok) {
		if (response.status === 404) {
			throw new NoCloudBackupError();
		}
		throw new Error("Failed to fetch backup");
	}

	const data = (await response.json()) as Partial<CloudBackupResponse>;
	if (!(data.backup && data.bapId)) {
		throw new Error("No backup data found in cloud storage.");
	}

	return data as CloudBackupResponse;
}

/**
 * Decrypt the cloud backup with the user's password, prove the identity to
 * the server, persist the backup on this device, and return where to go.
 * The password never leaves the browser; only the signature does.
 */
export async function unlockCloudBackup(input: {
	cloudBackup: CloudBackupResponse;
	password: string;
	returnTo: string | null;
	isCurrent?: () => boolean;
}): Promise<string | null> {
	try {
		const result = await authenticateImportedBackup(
			input.cloudBackup.backup,
			input.password,
			input.isCurrent ?? (() => true),
			{ selectedBapId: input.cloudBackup.bapId }
		);
		completeRecoveryMethod();
		return result.redirectURI || input.returnTo || "/";
	} catch (error) {
		if (error instanceof LoginFlowError) {
			throw new RestoreAuthorizationError(
				error.message,
				error.code,
				error.retryable
			);
		}
		throw error;
	}
}
